import { useMutation } from '@apollo/client';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { DELETE_IMPACT_CLAIMS } from 'graphql/secure/mutations';
import { GET_IMPACT_CLAIMS } from 'graphql/secure/queries';
import { ImpactClaim } from 'FeatureModules/Request/types/impactClaim.types';
import { IGraphQlConnection } from 'types/core.types';
import filter from 'lodash/filter';

const useImpactClaimActions = () => {
  const { openDialog } = useDialog();
  const { logEvent } = useLogEvent();

  const { setSuccessMessage, setErrorMessage } = useMessages();

  const [deleteImpactClaims] = useMutation<
    { deleteImpactClaims: { ids: string[] } },
    { ids: string[] }
  >(DELETE_IMPACT_CLAIMS, {
    onCompleted: () => setSuccessMessage('Impact claim was successfully removed'),
    onError: () => setErrorMessage('There was an error removing your impact claim'),
    update: (cache, { data }) => {
      if (!data?.deleteImpactClaims) return;

      const removedClaimIds = data.deleteImpactClaims.ids;

      const result = cache.readQuery<{ impactClaims: IGraphQlConnection<ImpactClaim> }>({
        query: GET_IMPACT_CLAIMS,
      });
      const impactClaims = result?.impactClaims || {
        edges: [],
      };

      cache.writeQuery({
        query: GET_IMPACT_CLAIMS,
        data: {
          impactClaims: {
            edges: filter(impactClaims.edges, ({ node }) => !removedClaimIds.includes(node.id)),
          },
        },
      });
    },
  });

  const handleAdd = () => {
    logEvent('CLICK_ADD_CLAIM');
    openDialog({ type: 'CREATE_IMPACT_CLAIM' });
  };
  const handleEdit = (claim: ImpactClaim) =>
    openDialog({ type: 'EDIT_IMPACT_CLAIM', props: { claim } });

  const handleDelete = (id: string) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete impact claim',
        text: 'Deleting this claim will remove it from any content.',
        submitText: 'Delete',
        onSubmit: async () => {
          await deleteImpactClaims({
            variables: {
              ids: [id],
            },
          });
        },
      },
    });
  };

  return {
    handleAdd,
    handleEdit,
    handleDelete,
  };
};

export default useImpactClaimActions;
